/**
 * 定时任务辅助工具
 * 提供定时任务的恢复和管理功能
 */

/**
 * 恢复定时任务的定时器
 * @param {Object} scheduledJobs - 定时任务对象
 * @param {Function} executeScheduledJob - 执行定时任务的函数
 * @param {Function} updateScheduledJob - 更新定时任务状态的函数
 * @param {Function} addLog - 添加日志的函数
 * @param {Object} existingTimers - 已存在的定时器（可选）
 * @returns {Object} 定时器引用对象
 */
export const restoreScheduledJobTimers = (scheduledJobs, executeScheduledJob, updateScheduledJob, addLog, existingTimers = {}) => {
  const now = new Date()
  const timers = { ...existingTimers }

  Object.values(scheduledJobs).forEach(job => {
    // 如果定时器已存在，跳过
    if (timers[job.id]) {
      addLog(`定时任务 ${job.id} 的定时器已存在，跳过创建`, 'info')
      return
    }

    if (job.status === 'scheduled') {
      const scheduledTime = new Date(job.scheduledTime)
      
      if (scheduledTime > now) {
        // 任务还未到执行时间，重新设置定时器
        const timeUntilExecution = scheduledTime.getTime() - now.getTime()
        const timerId = setTimeout(() => {
          executeScheduledJob(job.id)
        }, timeUntilExecution)
        
        timers[job.id] = timerId
        addLog(`已恢复定时任务 ${job.id}，将在 ${scheduledTime.toLocaleString()} 执行`, 'info')
      } else {
        // 任务已过了预定执行时间，重新安排到下一个执行时间
        const nextExecution = calculateNextExecutionTime(
          job.originalScheduleTime || '09:00',
          now
        )
        
        const timeUntilNextExecution = nextExecution.getTime() - now.getTime()
        const timerId = setTimeout(() => {
          executeScheduledJob(job.id)
        }, timeUntilNextExecution)
        
        timers[job.id] = timerId
        
        // 更新任务的下次执行时间
        updateScheduledJob(job.id, { 
          scheduledTime: nextExecution.toISOString()
        })
        
        addLog(`定时任务 ${job.id} 已错过执行时间，重新安排到 ${nextExecution.toLocaleString()}`, 'warning')
      }
    }
  })

  return timers
}

/**
 * 清除所有定时器
 * @param {Object} timers - 定时器引用对象
 */
export const clearAllTimers = (timers) => {
  Object.values(timers).forEach(timerId => {
    if (timerId) clearTimeout(timerId)
  })
}

/**
 * 计算下次执行时间
 * @param {string} originalScheduleTime - 原始时间设置 (HH:MM)
 * @param {Date} baseDate - 基准日期，默认为当前时间
 * @returns {Date} 下次执行时间
 */
export const calculateNextExecutionTime = (originalScheduleTime, baseDate = new Date()) => {
  const [hours, minutes] = originalScheduleTime.split(':').map(Number)
  const nextExecution = new Date(baseDate)
  nextExecution.setHours(hours, minutes, 0, 0)
  
  // 如果时间已过，安排到明天
  if (nextExecution <= baseDate) {
    nextExecution.setDate(nextExecution.getDate() + 1)
  }
  
  return nextExecution
}

/**
 * 验证定时任务数据的完整性
 * @param {Object} job - 定时任务对象
 * @returns {boolean} 是否有效
 */
export const validateScheduledJob = (job) => {
  return (
    job &&
    job.id &&
    job.tasks &&
    Array.isArray(job.tasks) &&
    job.accounts &&
    Array.isArray(job.accounts) &&
    job.configs &&
    job.scheduledTime &&
    job.status
  )
}

/**
 * 格式化定时任务信息用于显示
 * @param {Object} job - 定时任务对象
 * @returns {Object} 格式化后的信息
 */
export const formatScheduledJobInfo = (job) => {
  const scheduledTime = new Date(job.scheduledTime)
  const createdAt = job.createdAt ? new Date(job.createdAt) : null
  const lastExecutedAt = job.lastExecutedAt ? new Date(job.lastExecutedAt) : null
  
  return {
    id: job.id,
    scheduledTimeFormatted: scheduledTime.toLocaleString(),
    createdAtFormatted: createdAt ? createdAt.toLocaleString() : '未知',
    lastExecutedAtFormatted: lastExecutedAt ? lastExecutedAt.toLocaleString() : '从未执行',
    taskCount: job.tasks.length,
    accountCount: job.accounts.length,
    status: job.status,
    isOverdue: scheduledTime < new Date() && job.status === 'scheduled'
  }
}